/**
 * Which line of an imagined scene is playing (DOM-free, tested in scene-lines.test.ts). The scene's audio is one
 * clip with no cues, so each line's share of it is estimated from its length, plus a fixed pause between turns.
 * It only decides when a line counts as heard (its glue words, once a line a day on the server), never what the
 * learner sees, so an estimate is enough.
 */

/** Characters a turn's pause is worth. */
const TURN_PAUSE = 8;

/** Where each line starts, as a share of the whole clip (0 for the first, ascending, all below 1). */
export function lineStarts(texts: readonly string[]): number[] {
  const weights = texts.map((t) => Math.max(1, t.trim().length) + TURN_PAUSE);
  const total = weights.reduce((a, b) => a + b, 0);
  let at = 0;
  return weights.map((w) => {
    const start = total ? at / total : 0;
    at += w;
    return start;
  });
}

/** The line playing at `time` seconds of a clip `duration` seconds long; -1 with no line or no duration yet. */
export function lineAt(starts: readonly number[], time: number, duration: number): number {
  if (!starts.length || !(duration > 0) || !Number.isFinite(duration)) return -1;
  const share = Math.max(0, time) / duration;
  let i = 0;
  while (i + 1 < starts.length && starts[i + 1] <= share) i++;
  return i;
}

/** A line the learner says: the server stores a typed moment's learner lines as "You" ("Learner" from before). */
export function isYou(speaker: string | undefined): boolean {
  return /^\s*(you|learner)\s*$/i.test(speaker ?? '');
}

/**
 * The reading-pace floor: least time a scene line stays on screen as the one being said, so a voice that fails or
 * ends at once (no device voices, mock mode, a 402 or 501 fallback) never races the lines past unread.
 */
export const LINE_FLOOR_MIN_MS = 1200;
export const LINE_FLOOR_MS_PER_CHAR = 40;

/** How long a line of `text` is held at least: max(1.2 s, 40 ms a character). */
export function lineFloorMs(text: string): number {
  return Math.max(LINE_FLOOR_MIN_MS, LINE_FLOOR_MS_PER_CHAR * [...text.trim()].length);
}

/** The pacer's clock: the time now, and a timer that can be cancelled (injected, so tests run on a fake one). */
export interface PaceClock {
  now(): number;
  after(ms: number, fn: () => void): () => void;
}

/** The browser's clock, for the pacer. */
export const realClock: PaceClock = {
  now: () => Date.now(),
  after: (ms, fn) => { const t = setTimeout(fn, ms); return () => clearTimeout(t); },
};

/**
 * Shows a scene's lines as the voice reaches them, but never sooner than the line before has had its floor
 * (`floors[i]`, from `lineFloorMs`). `reach(i)`: the voice has reached line i. `end()`: the voice is over (ended,
 * failed or cut by its cap); `done` resolves once every line it reached has shown and the last has had its floor.
 * `stop()` ends it at once, showing nothing more.
 */
export function scenePacer(floors: readonly number[], show: (i: number) => void, clock: PaceClock) {
  let reached = -1;
  let shown = -1;
  let readyAt = -Infinity;
  let voiceOver = false;
  let over = false;
  let cancel: (() => void) | null = null;
  let finish = () => {};
  const done = new Promise<void>((resolve) => { finish = resolve; });
  const end = () => {
    if (over) return;
    over = true;
    cancel?.();
    cancel = null;
    finish();
  };
  const step = () => {
    if (over || cancel) return;
    while (shown < reached && clock.now() >= readyAt && !over) {
      shown++;
      readyAt = clock.now() + (floors[shown] ?? 0);
      show(shown);
    }
    if (over) return;
    if (shown >= reached && !voiceOver) return;
    const wait = readyAt - clock.now();
    if (wait > 0) cancel = clock.after(wait, () => { cancel = null; step(); });
    else if (shown >= reached) end();
  };
  return {
    done,
    reach(i: number) {
      reached = Math.max(reached, Math.min(i, floors.length - 1));
      step();
    },
    end() {
      voiceOver = true;
      step();
    },
    stop: end,
  };
}
